import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Calendar } from 'lucide-react';

import { MasterLookupBar, MasterCodeLookup } from '@/components/master/MasterLookupBar';
import { MasterPageHeader }   from '@/components/master/MasterPageHeader';
import { MasterFlash }         from '@/components/master/MasterFlash';
import { MasterAuditTab }      from '@/components/master/MasterAuditTab';
import { MasterConfirmDialogs } from '@/components/master/MasterConfirmDialogs';
import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { ReportHeader }        from '@/components/master/report/ReportHeader';
import { ReportSection }       from '@/components/master/report/ReportSection';
import { ReportFooter }        from '@/components/master/report/ReportFooter';
import {
  Badge, Button, Card, ConfirmDialog, Field, Input, Tabs, Textarea, Toggle,
} from '@/components/ui';

import { useAuth }             from '@/hooks/useAuth';
import { usePermission }       from '@/hooks/usePermission';
import { useFlashMessage }     from '@/hooks/useFlashMessage';
import { useMasterScreenState } from '@/hooks/useMasterScreenState';
import { useMasterRecord, masterListFetcher } from '@/hooks/useMasterRecord';
import { Permissions }         from '@/constants/permissions';
import { financialPeriodApi }  from '@/api/financialPeriod.api';
import type { FinancialPeriodDto, FinancialPeriodListDto } from '@/types/financialPeriod.types';
import { cn } from '@/utils/cn';
import { formatDateTime } from '@/utils/format';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  periodCode:     z.string().min(1).max(20),
  periodName:     z.string().min(1, 'Period name is required').max(100),
  fiscalYear:     z.coerce.number().int().min(2000).max(2100),
  periodNumber:   z.coerce.number().int().min(1).max(13),
  startDate:      z.string().min(1, 'Start date is required'),
  endDate:        z.string().min(1, 'End date is required'),
  postingAllowed: z.boolean(),
  remarks:        z.string().max(500).nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  periodCode: '', periodName: '', fiscalYear: new Date().getFullYear(),
  periodNumber: 1, startDate: '', endDate: '',
  postingAllowed: true, remarks: null,
};

/**
 * A period is identified by its year and its number, not by a name anyone types.
 *
 * `FinancialPeriod.Create` builds both the code and the name itself and ignores whatever the
 * client sends, so these mirror it exactly. Showing the derived values as the user types is the
 * whole point: the screen used to accept a code, promise to create it, and then file the record
 * under a different one.
 */
const PERIOD_CODE = /^(\d{4})-(\d{1,2})$/;

/** The thirteenth period is the adjustment period, and is named for the year rather than a month. */
const ADJUSTMENT_PERIOD = 13;

const derivedCode = (fiscalYear: number, periodNumber: number): string =>
  Number.isFinite(fiscalYear) && Number.isFinite(periodNumber)
    ? `${fiscalYear}-${String(periodNumber).padStart(2, '0')}`
    : '';

const derivedName = (fiscalYear: number, periodNumber: number, startDate: string): string => {
  if (periodNumber === ADJUSTMENT_PERIOD) return `Adjustment Period ${fiscalYear}`;
  if (!startDate) return '';
  // Built from the parts rather than `new Date(startDate)`: that parses as UTC midnight and, west
  // of Greenwich, renders as the month before.
  const [year, month] = startDate.split('-').map(Number);
  if (!year || !month) return '';
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

/** A typed code the screen can honour, or null if it cannot. */
function parsePeriodCode(code: string): { fiscalYear: number; periodNumber: number } | null {
  const match = PERIOD_CODE.exec(code.trim());
  if (!match) return null;
  const fiscalYear = Number(match[1]);
  const periodNumber = Number(match[2]);
  if (fiscalYear < 2000 || fiscalYear > 2100) return null;
  if (periodNumber < 1 || periodNumber > ADJUSTMENT_PERIOD) return null;
  return { fiscalYear, periodNumber };
}

const TABS = ['General', 'Status', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const SEARCH_COLUMNS: SearchColumn<FinancialPeriodListDto>[] = [
  { key: 'periodCode',   label: 'Code',    className: 'w-24 font-mono' },
  { key: 'periodName',   label: 'Name' },
  { key: 'fiscalYear',   label: 'Year',    className: 'w-16 text-center' },
  { key: 'periodNumber', label: '#',       className: 'w-10 text-center' },
  { key: 'startDate',    label: 'Start',   className: 'w-28' },
  { key: 'endDate',      label: 'End',     className: 'w-28' },
  { key: 'isClosed', label: 'Status', className: 'w-20',
    render: r => <Badge variant={r.isClosed ? 'default' : 'success'}>{r.isClosed ? 'Closed' : 'Open'}</Badge> },
];

function dtoToForm(dto: FinancialPeriodDto): FormValues {
  return {
    periodCode:     dto.periodCode,
    periodName:     dto.periodName,
    fiscalYear:     dto.fiscalYear,
    periodNumber:   dto.periodNumber,
    startDate:      dto.startDate,
    endDate:        dto.endDate,
    postingAllowed: dto.postingAllowed,
    remarks:        dto.remarks ?? null,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export const FinancialPeriodPage = () => {
  const { user }          = useAuth();
  const { hasPermission } = usePermission();

  const canView   = hasPermission(Permissions.MST_FINANCIAL_PERIOD_VIEW);
  const canCreate = hasPermission(Permissions.MST_FINANCIAL_PERIOD_CREATE);
  const canEdit   = hasPermission(Permissions.MST_FINANCIAL_PERIOD_EDIT);
  const canDelete = hasPermission(Permissions.MST_FINANCIAL_PERIOD_DELETE);
  const canClose  = hasPermission(Permissions.MST_FINANCIAL_PERIOD_CLOSE);
  const canReopen = hasPermission(Permissions.MST_FINANCIAL_PERIOD_REOPEN);

  const screen = useMasterScreenState({ canEdit, canDelete });

  const { flashMsg, flash } = useFlashMessage();
  const codeInputRef = useRef<HTMLInputElement>(null);

  const [codeInput,         setCodeInput]       = useState('');

  const [showNewConfirm,        setShowNewConfirm]        = useState<string | null>(null);
  const [showCloseConfirm,      setShowCloseConfirm]      = useState(false);
  const [showReopenConfirm,     setShowReopenConfirm]     = useState(false);
  const [actionBusy,            setActionBusy]            = useState(false);

  const {
    register, handleSubmit, control, reset, watch, setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_FORM,
  });

  const postingAllowed = watch('postingAllowed');

  // The code and the name are the server's to decide. Watching the three inputs it decides from
  // means the two read-only fields below always show what will actually be saved.
  const fiscalYear   = watch('fiscalYear');
  const periodNumber = watch('periodNumber');
  const startDate    = watch('startDate');

  useEffect(() => {
    if (!screen.isNew) return;
    setValue('periodCode', derivedCode(Number(fiscalYear), Number(periodNumber)));
    setValue('periodName', derivedName(Number(fiscalYear), Number(periodNumber), startDate));
  }, [screen.isNew, fiscalYear, periodNumber, startDate, setValue]);

  const master = useMasterRecord<FinancialPeriodDto, Tab, FormValues>({
    screen, flash, firstTab: 'General', focusRef: codeInputRef,
    clearForm:   () => reset(EMPTY_FORM),
    clearScreen: () => { setCodeInput(''); },
    onDelete: {
      remove:   id => financialPeriodApi.remove(id),
      describe: r => `Period '${r.periodCode}'`,
    },
  });

  const {
    record, setRecord, activeTab, setActiveTab, saving, setSaving, lookupBusy, setLookupBusy,
    printOpen, setPrintOpen, searchOpen, setSearchOpen,
    pendingSave, setPendingSave, showSaveConfirm, setShowSaveConfirm,
    showDeleteConfirm, setShowDeleteConfirm, resetToInitial, executeDelete, beginSave,
  } = master;


  const loadRecord = useCallback((dto: FinancialPeriodDto) => {
    setRecord(dto); reset(dtoToForm(dto));
    setCodeInput(dto.periodCode); screen.setMode('view'); setActiveTab('General');
  }, [reset, screen]);

  const handleCodeLookup = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    if (!canView) { flash('error', 'You do not have permission to view financial periods.'); return; }
    setLookupBusy(true);
    try {
      const res = await financialPeriodApi.getByCode(code);
      if (res.data.data) loadRecord(res.data.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        if (!canCreate) { flash('error', `Period '${code}' was not found.`); return; }
        // Only offer to create a code that will actually come back as typed. Anything else was
        // being accepted, promised, and then quietly filed under a code derived from the form.
        if (!parsePeriodCode(code)) {
          flash('error',
            `'${code}' is not a period code. Use year and period number, such as 2026-01 ` +
            `(or 2026-13 for the adjustment period).`);
          return;
        }
        setShowNewConfirm(code);
      } else {
        flash('error', 'Lookup failed. Please try again.');
      }
    } finally {
      setLookupBusy(false);
    }
  };

  const handleConfirmCreateNew = () => {
    const code = showNewConfirm; setShowNewConfirm(null);
    if (!code) return;
    const parsed = parsePeriodCode(code);
    if (!parsed) return;
    // Seed the two fields the code is built from, so the record really is filed under the code
    // the dialog just named.
    reset({
      ...EMPTY_FORM,
      periodCode:   code,
      fiscalYear:   parsed.fiscalYear,
      periodNumber: parsed.periodNumber,
    });
    setCodeInput(code);
    setRecord(null); screen.setMode('new'); setActiveTab('General');
  };

  const handleSaveClick = () => {
    // The second argument runs when the form is invalid. Without it a failed validation did
    // nothing at all -- no save, no message, no focus -- so the button looked broken rather than
    // the form looking wrong.
    handleSubmit(beginSave, (formErrors) => {
      const first = Object.values(formErrors)[0]?.message;
      flash('error', first ? String(first) : 'Please correct the highlighted fields.');
    })();
  };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false); setSaving(true);
    try {
      const d = pendingSave;
      if (screen.isNew) {
        await financialPeriodApi.create({
          fiscalYear:    d.fiscalYear,
          periodNumber:  d.periodNumber,
          startDate:     d.startDate,
          endDate:       d.endDate,
          remarks:       d.remarks?.trim() || null,
        });
        flash('success', 'Financial period created successfully.');
        resetToInitial();
      } else if (screen.isEdit && record) {
        await financialPeriodApi.update(record.id, {
          periodName:     d.periodName.trim(),
          postingAllowed: d.postingAllowed,
          remarks:        d.remarks?.trim() || null,
        });
        flash('success', 'Financial period updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      const body = (err as { response?: { data?: { message?: string;
                       errors?: { message?: string }[] } } }).response?.data;
      // errors[0] names the field. The envelope's own message is just "Validation failed."
      const msg = body?.errors?.[0]?.message ?? body?.message ?? 'Save failed.';
      flash('error', msg);
    } finally {
      setSaving(false); setPendingSave(null);
    }
  };

  const executeClose = async () => {
    if (!record) return;
    setShowCloseConfirm(false); setActionBusy(true);
    try {
      await financialPeriodApi.close(record.id);
      flash('success', `Period '${record.periodCode}' has been closed.`);
      // Reload the record
      const res = await financialPeriodApi.getById(record.id);
      if (res.data.data) loadRecord(res.data.data);
    } catch (err: unknown) {
      const body = (err as { response?: { data?: { message?: string;
                       errors?: { message?: string }[] } } }).response?.data;
      // errors[0] names the field. The envelope's own message is just "Validation failed."
      const msg = body?.errors?.[0]?.message ?? body?.message ?? 'Close failed.';
      flash('error', msg);
    } finally { setActionBusy(false); }
  };

  const executeReopen = async () => {
    if (!record) return;
    setShowReopenConfirm(false); setActionBusy(true);
    try {
      await financialPeriodApi.reopen(record.id);
      flash('success', `Period '${record.periodCode}' has been reopened.`);
      const res = await financialPeriodApi.getById(record.id);
      if (res.data.data) loadRecord(res.data.data);
    } catch (err: unknown) {
      const body = (err as { response?: { data?: { message?: string;
                       errors?: { message?: string }[] } } }).response?.data;
      // errors[0] names the field. The envelope's own message is just "Validation failed."
      const msg = body?.errors?.[0]?.message ?? body?.message ?? 'Reopen failed.';
      flash('error', msg);
    } finally { setActionBusy(false); }
  };


  const fetchPeriods = useMemo(() => masterListFetcher(financialPeriodApi.getList), []);

  const handleSearchSelect = (item: FinancialPeriodListDto) => {
    setSearchOpen(false); setLookupBusy(true);
    financialPeriodApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load period.'))
      .finally(() => setLookupBusy(false));
  };

  const fieldsDisabled = screen.isInitial || screen.isView;

  const statusBadgeClass = (closed: boolean) =>
    closed ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
           : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">

      <MasterPageHeader icon={Calendar} title="Financial Period Maintenance"
        subtitle="Manage accounting periods and posting status" screen={screen} />

      <MasterToolbar
        {...screen.toolbar} {...master.toolbar}
        saving={saving} loading={lookupBusy} onSave={handleSaveClick}
      />

      <MasterFlash message={flashMsg} />

      <MasterLookupBar
        label="Period Code"
        summary={record && (<>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{record.periodName}</span>
            <Badge variant={record.isClosed ? 'default' : 'success'}>{record.isClosed ? 'Closed' : 'Open'}</Badge>
            {!record.postingAllowed && (
              <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold">
                NO POSTING
              </span>
            )}
        </>)}
        hint={screen.isInitial ? "Enter period code (e.g. 2026-01) and press Enter, or browse." : undefined}
      >
        <MasterCodeLookup
          inputRef={codeInputRef}
          value={codeInput}
          onChange={setCodeInput}
          onSubmit={handleCodeLookup}
          onBrowse={() => setSearchOpen(true)}
          browseLabel="Search periods"
          placeholder="Type period code + Enter…"
          disabled={screen.isNew || screen.isEdit || saving}
          busy={lookupBusy}
        />
      </MasterLookupBar>

      {/* Main content */}
      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

          <div className="p-5 space-y-5 max-w-2xl">

            {activeTab === 'General' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Period Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Both of these belong to the server. It builds the code from the year and
                      the period number, and the name from the start date, so they are shown here
                      rather than asked for — and they update as those three fields change. */}
                  <Input
                    label="Period Code"
                    {...register('periodCode')}
                    disabled
                    placeholder="— from year and period number —"
                    className="font-mono uppercase"
                    hint={screen.isNew ? 'Built from the year and period number below.' : undefined}
                  />
                  {/* No asterisk: nobody supplies this. The server names the period from its
                      start date, and the field only becomes editable once the record exists. */}
                  <Input
                    label="Period Name"
                    {...register('periodName')}
                    error={errors.periodName?.message}
                    disabled={fieldsDisabled || screen.isNew}
                    placeholder="e.g. January 2026"
                    hint={screen.isNew ? 'Taken from the start date. Editable once saved.' : undefined}
                  />
                  <Input label="Fiscal Year *" type="number" {...register('fiscalYear')}
                    disabled={fieldsDisabled || screen.isEdit} min={2000} max={2100}
                    error={errors.fiscalYear?.message} />
                  <Input label="Period Number *" type="number" {...register('periodNumber')}
                    disabled={fieldsDisabled || screen.isEdit} min={1} max={13}
                    error={errors.periodNumber?.message} />
                  <Input label="Start Date *" type="date" {...register('startDate')}
                    disabled={fieldsDisabled || screen.isEdit} error={errors.startDate?.message} />
                  <Input label="End Date *" type="date" {...register('endDate')}
                    disabled={fieldsDisabled || screen.isEdit} error={errors.endDate?.message} />
                  <Field label="Posting Allowed" className="sm:col-span-2">
                    <Controller name="postingAllowed" control={control} render={({ field }) => (
                      <Toggle
                        label={postingAllowed ? 'Posting allowed in this period' : 'Posting blocked'}
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={fieldsDisabled}
                      />
                    )} />
                  </Field>
                  <Textarea label="Remarks" {...register('remarks')} disabled={fieldsDisabled}
                    rows={3} maxLength={500} placeholder="Optional notes…"
                    className="resize-none" fieldClassName="sm:col-span-2" />
                </div>
              </Card>
            )}

            {activeTab === 'Status' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Period Status</h3>
                {record ? (
                  <>
                    <div className="grid grid-cols-2 gap-3 mb-6">
                      {[
                        { label: 'GL Closed',       closed: record.isGlClosed },
                        { label: 'AR Closed',       closed: record.isArClosed },
                        { label: 'AP Closed',       closed: record.isApClosed },
                        { label: 'Banking Closed',  closed: record.isBankingClosed },
                      ].map(({ label, closed }) => (
                        <div key={label} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{label}</span>
                          <span className={cn('px-2 py-0.5 rounded text-[10px] font-semibold', statusBadgeClass(closed))}>
                            {closed ? 'Closed' : 'Open'}
                          </span>
                        </div>
                      ))}
                      <div className="col-span-2 flex items-center justify-between p-3 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                        <span className="text-xs font-bold text-gray-700 dark:text-gray-300">Overall Status</span>
                        <span className={cn('px-2 py-0.5 rounded text-[10px] font-bold', statusBadgeClass(record.isClosed))}>
                          {record.isClosed ? 'CLOSED' : 'OPEN'}
                        </span>
                      </div>
                    </div>

                    {/* Action buttons — VIEW mode only */}
                    {screen.isView && (
                      <div className="flex gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                        {canClose && !record.isClosed && (
                          <Button variant="danger" size="sm" className="text-xs font-semibold"
                            onClick={() => setShowCloseConfirm(true)} loading={actionBusy}>
                            Close Period
                          </Button>
                        )}
                        {canReopen && record.isClosed && (
                          <Button variant="success" size="sm" className="text-xs font-semibold"
                            onClick={() => setShowReopenConfirm(true)} loading={actionBusy}>
                            Reopen Period
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-400 italic">Load a period to view its status.</p>
                )}
              </Card>
            )}

            {activeTab === 'Audit' && <MasterAuditTab record={record} />}
          </div>
        </form>
      </div>

      {/* Modals */}
      <MasterSearchModal<FinancialPeriodListDto>
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        title="Browse Financial Periods"
        columns={SEARCH_COLUMNS}
        fetchData={fetchPeriods}
        onSelect={handleSearchSelect}
      />

      <MasterConfirmDialogs
        entity="Financial period"
        screen={screen}
        record={record}
        describe={r => `${r.periodCode} — ${r.periodName}`}
        saving={saving}
        create={{
          label: showNewConfirm,
          note: 'The period will be named from the start date you enter.',
          onConfirm: handleConfirmCreateNew,
          onCancel: () => setShowNewConfirm(null),
        }}
        saveOpen={showSaveConfirm}
        onSaveConfirm={executeSave}
        onSaveCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
        deleteOpen={showDeleteConfirm}
        onDeleteConfirm={executeDelete}
        onDeleteCancel={() => setShowDeleteConfirm(false)}
      />

      <ConfirmDialog
        isOpen={showCloseConfirm} variant="delete"
        title="Close Financial Period"
        message={<span>Are you sure you want to <strong>close</strong> period <strong>{record?.periodCode} — {record?.periodName}</strong>?<br /><span className="text-xs text-gray-400">No new postings will be allowed after closing.</span></span>}
        confirmLabel="Yes, Close Period" cancelLabel="Cancel" loading={actionBusy}
        onConfirm={executeClose} onCancel={() => setShowCloseConfirm(false)}
      />

      <ConfirmDialog
        isOpen={showReopenConfirm} variant="info"
        title="Reopen Financial Period"
        message={<span>Reopen period <strong>{record?.periodCode} — {record?.periodName}</strong>?</span>}
        confirmLabel="Yes, Reopen" cancelLabel="Cancel" loading={actionBusy}
        onConfirm={executeReopen} onCancel={() => setShowReopenConfirm(false)}
      />

      {record && (
        <MasterReportPreview isOpen={printOpen} onClose={() => setPrintOpen(false)} title="Financial Period Report">
          <ReportHeader
            reportTitle="Financial Period Master Report"
            printedBy={user?.fullName ?? user?.username}
            printedAt={new Date()}
            meta={[
              { label: 'Period Code', value: record.periodCode },
              { label: 'Status',      value: record.isClosed ? 'Closed' : 'Open' },
            ]}
          />
          <ReportSection
            title="Period Details"
            fields={[
              { label: 'Period Code',    value: record.periodCode },
              { label: 'Period Name',    value: record.periodName, wide: true },
              { label: 'Fiscal Year',    value: record.fiscalYear },
              { label: 'Period Number',  value: record.periodNumber },
              { label: 'Start Date',     value: record.startDate },
              { label: 'End Date',       value: record.endDate },
              { label: 'Posting Allowed', value: record.postingAllowed, display: record.postingAllowed ? 'Yes' : 'No' },
            ]}
          />
          <ReportSection
            title="Closure Status"
            fields={[
              { label: 'Overall',  value: record.isClosed, display: record.isClosed ? 'Closed' : 'Open' },
              { label: 'GL',       value: record.isGlClosed, display: record.isGlClosed ? 'Closed' : 'Open' },
              { label: 'AR',       value: record.isArClosed, display: record.isArClosed ? 'Closed' : 'Open' },
              { label: 'AP',       value: record.isApClosed, display: record.isApClosed ? 'Closed' : 'Open' },
              { label: 'Banking',  value: record.isBankingClosed, display: record.isBankingClosed ? 'Closed' : 'Open' },
              { label: 'Closed By',   value: record.closedBy },
              { label: 'Closed Date', value: formatDateTime(record.closedDate) },
            ]}
          />
          <ReportSection
            title="Audit Information"
            fields={[
              { label: 'Created By',    value: record.createdByName ?? record.createdBy },
              { label: 'Created Date',  value: formatDateTime(record.createdDate) },
              { label: 'Modified By',   value: record.modifiedByName ?? record.modifiedBy },
              { label: 'Modified Date', value: formatDateTime(record.modifiedDate) },
            ]}
          />
          <ReportFooter />
        </MasterReportPreview>
      )}
    </div>
  );
};
